// frontend/app/exhibit/v2/AIExhibitV2App.js

import React, { useCallback, useEffect, useRef, useState } from "react";
import { Pressable, StyleSheet, Text, View } from "react-native";

import ExhibitV2Stage from "./components/ExhibitV2Stage";
import AttractScreen from "./screens/AttractScreen";
import AudienceScreen from "./screens/AudienceScreen";
import HubScreen from "./screens/HubScreen";
import TeachMachineScreen from "./screens/TeachMachineScreen";
import RealOrAIScreen from "./screens/RealOrAIScreen";
import NextWordScreen from "./screens/NextWordScreen";
import AITodayScreen from "./screens/AITodayScreen";
import FutureScreen from "./screens/FutureScreen";
import WhereAIScreen from "./screens/WhereAIScreen";
import OutsmartScreen from "./screens/OutsmartScreen";
import TrainLabScreen from "./screens/TrainLabScreen";
import CoachScreen from "./screens/CoachScreen";
import BluffScreen from "./screens/BluffScreen";
import FeedScreen from "./screens/FeedScreen";
import DialScreen from "./screens/DialScreen";
import RelayScreen from "./screens/RelayScreen";
import AskSarasScreen from "./screens/AskSarasScreen";
import ScoreboardScreen from "./screens/ScoreboardScreen";
import LibraryBridgeScreen from "./screens/LibraryBridgeScreen";
import {
  createExhibitAnalyticsId,
  endExhibitSession,
  flushExhibitAnalyticsOutbox,
  logExhibitAttempt,
  logExhibitEvent,
  startExhibitSession,
  updateExhibitSession,
} from "../api/aiExhibitAnalyticsClient";
import { exhibitLanguageFromUrl, normalizeExhibitLanguage, tx } from "./i18n/exhibitLocale";
import { ExhibitLocaleProvider } from "./i18n/ExhibitLocaleContext";
import {
  playAIExhibitV2FixedNarration,
  stopAIExhibitV2FixedNarration,
} from "./audio/useAIExhibitV2FixedNarration";

const DEFAULT_IDLE_MS = 3 * 60 * 1000;
const IDLE_WARNING_MS = 20 * 1000;
const DEFAULT_KIOSK_EMAIL = "ai.explained.davinci.kiosk.1@saras-3d.com";
const DEFAULT_KIOSK_ID = "davinci_01";
const EXHIBIT_VERSION = "2.0";
const CONTENT_VERSION = "v2_bilingual_rc1";

const INTRO_NARRATION_BY_SCREEN = Object.freeze({
  attract: "v2_attract_intro",
  audience: "v2_audience_intro",
  hub: "v2_hub_intro",
  teach: "v2_teach_intro",
  "real-or-ai": "v2_real_or_ai_intro",
  "next-word": "v2_next_word_intro",
  today: "v2_ai_today_intro",
  future: "v2_future_intro",
  "where-ai": "v2_where_ai_intro",
  outsmart: "v2_outsmart_intro",
  "train-lab": "v2_train_lab_intro",
  coach: "v2_coach_intro",
  bluff: "v2_bluff_intro",
  feed: "v2_feed_intro",
  dial: "v2_dial_intro",
  relay: "v2_relay_intro",
  ask: "v2_ask_intro",
  scoreboard: "v2_scoreboard_intro",
  library: "v2_library_intro",
});

function createVisitorSessionId() {
  return createExhibitAnalyticsId();
}

function idleDurationMs() {
  if (typeof window === "undefined") return DEFAULT_IDLE_MS;

  try {
    const params = new URL(window.location.href).searchParams;
    const seconds = Number.parseFloat(params.get("idle"));
    return Number.isFinite(seconds) && seconds > 0
      ? seconds * 1000
      : DEFAULT_IDLE_MS;
  } catch {
    return DEFAULT_IDLE_MS;
  }
}

function resolveKioskId(authenticatedUserId) {
  if (typeof window !== "undefined") {
    try {
      const params = new URL(window.location.href).searchParams;
      const configured = String(params.get("ai_exhibit_kiosk_id") || "").trim();
      if (configured) return configured;
    } catch {}
  }

  return authenticatedUserId ? null : DEFAULT_KIOSK_ID;
}

export default function AIExhibitV2App({ authenticatedUserId = null }) {
  const [screen, setScreen] = useState("attract");
  const initialLanguageRef = useRef(exhibitLanguageFromUrl());
  const [language, setLanguage] = useState(() => initialLanguageRef.current);
  const [tier, setTier] = useState("hs");
  const [nextAfterAudience, setNextAfterAudience] = useState("hub");
  const [pendingRealOrAIVote, setPendingRealOrAIVote] = useState(null);
  const [visitorSessionId, setVisitorSessionId] = useState(() => createVisitorSessionId());
  const [askConversationStarted, setAskConversationStarted] = useState(false);
  const [idleWarning, setIdleWarning] = useState(false);
  const [audioEnabled, setAudioEnabled] = useState(true);

  const idleTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const sessionStartedRef = useRef(false);
  const lastHeartbeatRef = useRef(0);
  const lastPointerMoveRef = useRef(0);

  const normalizedAuthenticatedUserId =
    typeof authenticatedUserId === "string" ? authenticatedUserId.trim().toLowerCase() : "";
  const visitorUserId = normalizedAuthenticatedUserId || DEFAULT_KIOSK_EMAIL;
  const kioskId = resolveKioskId(normalizedAuthenticatedUserId);

  const ensureSessionStarted = useCallback(async () => {
    if (sessionStartedRef.current) return;
    sessionStartedRef.current = true;

    const result = await startExhibitSession({
      session_id: visitorSessionId,
      student_email: visitorUserId,
      kiosk_id: kioskId,
      exhibit_version: EXHIBIT_VERSION,
      content_version: CONTENT_VERSION,
      metadata_json: { source: "ai_explained_v2", language },
    });

    if (result?.queued) {
      console.warn("[AI EXPLAINED V2] session start queued; analytics will retry.");
    }
  }, [visitorSessionId, visitorUserId, kioskId, language]);

  const logEvent = useCallback(
    async (eventType, { stationId = null, screenId = null, payload = {} } = {}) => {
      await ensureSessionStarted();
      return logExhibitEvent({
        session_id: visitorSessionId,
        student_email: visitorUserId,
        event_type: eventType,
        station_id: stationId,
        screen_id: screenId,
        payload_json: payload,
      });
    },
    [ensureSessionStarted, visitorSessionId, visitorUserId]
  );

  const touchActiveSession = useCallback(() => {
    if (!sessionStartedRef.current) return;

    const now = Date.now();
    if (now - lastHeartbeatRef.current < 15000) return;
    lastHeartbeatRef.current = now;

    updateExhibitSession(visitorSessionId, {
      student_email: visitorUserId,
    });
  }, [visitorSessionId, visitorUserId]);

  const recordAttempt = useCallback(
    async ({ stationId, itemId, selectedAnswer, correctAnswer, isCorrect, attemptType = "binary_guess", contentVersion = null, metadata = {} }) => {
      await ensureSessionStarted();
      return logExhibitAttempt({
        session_id: visitorSessionId,
        student_email: visitorUserId,
        station_id: stationId,
        item_id: itemId,
        content_version: contentVersion || CONTENT_VERSION,
        attempt_type: attemptType,
        selected_answer: selectedAnswer,
        correct_answer: correctAnswer,
        is_correct: isCorrect,
        metadata_json: metadata,
      });
    },
    [ensureSessionStarted, visitorSessionId, visitorUserId]
  );

  const resetVisitor = useCallback(
    (reason = "idle_timeout") => {
      if (sessionStartedRef.current) {
        endExhibitSession(visitorSessionId, {
          student_email: visitorUserId,
          end_reason: reason,
        });
      }

      stopAIExhibitV2FixedNarration();
      setScreen("attract");
      setLanguage(initialLanguageRef.current);
      setAudioEnabled(true);
      setTier("hs");
      setNextAfterAudience("hub");
      setPendingRealOrAIVote(null);
      setVisitorSessionId(createVisitorSessionId());
      setAskConversationStarted(false);
      setIdleWarning(false);
      sessionStartedRef.current = false;
      lastHeartbeatRef.current = 0;
      lastPointerMoveRef.current = 0;
    },
    [visitorSessionId, visitorUserId]
  );

  useEffect(() => {
    if (typeof window === "undefined") return;

    flushExhibitAnalyticsOutbox();
    const onOnline = () => flushExhibitAnalyticsOutbox();
    window.addEventListener("online", onOnline);

    try {
      const params = new URL(window.location.href).searchParams;
      if (params.get("reset") === "1") {
        // One-time cleanup for prototype-era browser tallies. They are no longer
        // authoritative; live analytics now come from the backend/RDS.
        localStorage.removeItem("ai_exhibit_v2_tally_v1");
        localStorage.removeItem("ai_exhibit_v2_roa_tally_v1");
        localStorage.removeItem("ai_exhibit_v2_future_tally_v1");
      }
    } catch {}

    return () => window.removeEventListener("online", onOnline);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const clearTimers = () => {
      if (idleTimerRef.current) window.clearTimeout(idleTimerRef.current);
      if (warningTimerRef.current) window.clearTimeout(warningTimerRef.current);
      idleTimerRef.current = null;
      warningTimerRef.current = null;
    };

    const scheduleReset = () => {
      clearTimers();
      setIdleWarning(false);

      if (screen === "attract") return;
      touchActiveSession();

      const idleMs = idleDurationMs();
      warningTimerRef.current = window.setTimeout(
        () => setIdleWarning(true),
        Math.max(0, idleMs - IDLE_WARNING_MS)
      );
      idleTimerRef.current = window.setTimeout(() => resetVisitor("idle_timeout"), idleMs);
    };

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        resetVisitor("staff_escape");
        return;
      }
      scheduleReset();
    };

    const activityEvents = ["pointerdown", "touchstart", "wheel", "click"];
    activityEvents.forEach((name) =>
      window.addEventListener(name, scheduleReset, { passive: true })
    );

    // A visitor can be actively exploring with a mouse without clicking.
    // Pointer movement is throttled so we do not churn timers on every pixel.
    const onPointerMove = () => {
      const now = Date.now();
      if (now - lastPointerMoveRef.current < 2000) return;
      lastPointerMoveRef.current = now;
      scheduleReset();
    };

    window.addEventListener("pointermove", onPointerMove, { passive: true });
    window.addEventListener("keydown", onKeyDown);

    scheduleReset();

    return () => {
      activityEvents.forEach((name) => window.removeEventListener(name, scheduleReset));
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("keydown", onKeyDown);
      clearTimers();
    };
  }, [screen, resetVisitor, touchActiveSession]);

  const prepareNarrationForScreen = useCallback(
    (nextScreen, explicitNarrationId = null) => {
      stopAIExhibitV2FixedNarration();
      if (!audioEnabled) return;

      const narrationId = explicitNarrationId || INTRO_NARRATION_BY_SCREEN[nextScreen];
      if (!narrationId) return;

      // Start while still inside the visitor's press/click when possible.
      // This mirrors the proven V1 behavior and avoids autoplay surprises.
      playAIExhibitV2FixedNarration({
        audioEnabled: true,
        language,
        narrationId,
      });
    },
    [audioEnabled, language]
  );

  const navigateTo = useCallback(
    (nextScreen, options = {}) => {
      prepareNarrationForScreen(nextScreen, options.narrationId || null);
      setScreen(nextScreen);
    },
    [prepareNarrationForScreen]
  );

  const playNarrationMoment = useCallback(
    (narrationId) => {
      if (!audioEnabled || !narrationId) return;
      playAIExhibitV2FixedNarration({ audioEnabled: true, language, narrationId });
    },
    [audioEnabled, language]
  );

  const toggleAudio = useCallback(() => {
    if (audioEnabled) {
      stopAIExhibitV2FixedNarration();
      setAudioEnabled(false);
      return;
    }

    setAudioEnabled(true);
    const narrationId = INTRO_NARRATION_BY_SCREEN[screen];
    if (narrationId) {
      // The visitor just pressed Audio ON, so play the current screen's
      // instructional intro immediately inside that user gesture.
      playAIExhibitV2FixedNarration({
        audioEnabled: true,
        language,
        narrationId,
      });
    }
  }, [audioEnabled, language, screen]);

  useEffect(
    () => () => {
      stopAIExhibitV2FixedNarration();
    },
    []
  );

  const goAfterAudience = (target) => {
    if (target === "real-or-ai-reveal") {
      navigateTo("real-or-ai", { narrationId: "v2_real_or_ai_photo_reveal" });
      return;
    }
    navigateTo("hub");
  };

  const handleAttractChoice = (side, photoRound = null) => {
    const generated = photoRound?.generated || "B";
    const basePhotoContentVersion = photoRound?.contentVersion || CONTENT_VERSION;
    const photoContentVersion = language === "es"
      ? `${basePhotoContentVersion}_es`
      : basePhotoContentVersion;
    ensureSessionStarted().then(() =>
      recordAttempt({
        stationId: "real-or-ai",
        itemId: photoRound?.id || "photo",
        selectedAnswer: side,
        correctAnswer: generated,
        isCorrect: side === generated,
        contentVersion: photoContentVersion,
        metadata: { source: "attract", medium: "Photo", language },
      })
    );
    setPendingRealOrAIVote(side);
    setNextAfterAudience("real-or-ai-reveal");
    navigateTo("audience");
  };

  const handleAttractSkip = () => {
    ensureSessionStarted().then(() =>
      logEvent("session_started_from_attract", { screenId: "attract", payload: { skipped_guess: true } })
    );
    setPendingRealOrAIVote(null);
    setNextAfterAudience("hub");
    navigateTo("audience");
  };

  const handleAudienceSelect = (selectedTier) => {
    setTier(selectedTier);
    updateExhibitSession(visitorSessionId, {
      student_email: visitorUserId,
      audience_tier: selectedTier,
    });
    logEvent("audience_selected", { screenId: "audience", payload: { tier: selectedTier } });
    goAfterAudience(nextAfterAudience);
  };

  const handleAudienceSkip = () => {
    setTier("hs");
    updateExhibitSession(visitorSessionId, {
      student_email: visitorUserId,
      audience_tier: "hs",
    });
    logEvent("audience_skipped", { screenId: "audience", payload: { default_tier: "hs" } });
    goAfterAudience(nextAfterAudience);
  };

  const handleChangeTier = () => {
    setPendingRealOrAIVote(null);
    setNextAfterAudience("hub");
    navigateTo("audience");
  };

  const handleHubNavigate = (destination) => {
    logEvent("station_started", { stationId: destination, screenId: "hub" });

    if (destination === "teach") {
      navigateTo("teach");
      return;
    }

    if (destination === "real-or-ai") {
      setPendingRealOrAIVote(null);
      navigateTo("real-or-ai");
      return;
    }

    if (
      [
        "next-word",
        "today",
        "future",
        "where-ai",
        "outsmart",
        "train-lab",
        "coach",
        "bluff",
        "feed",
        "dial",
        "relay",
        "ask",
        "scoreboard",
        "library",
      ].includes(destination)
    ) {
      navigateTo(destination);
    }
  };

  const backToHub = () => {
    logEvent("returned_to_hub", { screenId: screen });
    setPendingRealOrAIVote(null);
    navigateTo("hub");
  };

  const changeLanguage = (nextLanguage) => {
    const normalized = normalizeExhibitLanguage(nextLanguage);
    if (normalized === language) return;
    stopAIExhibitV2FixedNarration();
    setLanguage(normalized);
    if (sessionStartedRef.current) {
      logEvent("language_changed", {
        screenId: screen,
        payload: { language: normalized },
      });
    }
  };

  const showLanguageToggle = ["attract", "audience", "hub"].includes(screen);

  return (
    <ExhibitLocaleProvider language={language}>
    <ExhibitV2Stage>
      {screen === "attract" && (
        <AttractScreen language={language} onChoose={handleAttractChoice} onSkip={handleAttractSkip} />
      )}

      {screen === "audience" && (
        <AudienceScreen language={language} onSelect={handleAudienceSelect} onSkip={handleAudienceSkip} />
      )}

      {screen === "hub" && (
        <HubScreen
          tier={tier}
          language={language}
          onNavigate={handleHubNavigate}
          onChangeTier={handleChangeTier}
          onNextVisitor={() => resetVisitor("explicit_next_visitor")}
        />
      )}

      {screen === "teach" && <TeachMachineScreen tier={tier} language={language} onBack={backToHub} onNarrationMoment={playNarrationMoment} />}

      {screen === "real-or-ai" && (
        <RealOrAIScreen
          tier={tier}
          language={language}
          onBack={backToHub}
          initialVote={pendingRealOrAIVote}
          startInReveal={Boolean(pendingRealOrAIVote)}
          onAttempt={recordAttempt}
          contentVersion={CONTENT_VERSION}
          onNarrationMoment={playNarrationMoment}
          onBeforeMediaAudio={stopAIExhibitV2FixedNarration}
        />
      )}

      {screen === "next-word" && <NextWordScreen tier={tier} language={language} onBack={backToHub} onNarrationMoment={playNarrationMoment} />}
      {screen === "today" && <AITodayScreen tier={tier} language={language} onBack={backToHub} onNarrationMoment={playNarrationMoment} />}
      {screen === "future" && <FutureScreen tier={tier} language={language} onBack={backToHub} onAttempt={recordAttempt} onNarrationMoment={playNarrationMoment} />}
      {screen === "where-ai" && <WhereAIScreen tier={tier} language={language} onBack={backToHub} onNarrationMoment={playNarrationMoment} />}
      {screen === "outsmart" && <OutsmartScreen tier={tier} language={language} onBack={backToHub} onAttempt={recordAttempt} onNarrationMoment={playNarrationMoment} />}
      {screen === "train-lab" && <TrainLabScreen tier={tier} language={language} onBack={backToHub} onAttempt={recordAttempt} onNarrationMoment={playNarrationMoment} />}
      {screen === "coach" && <CoachScreen tier={tier} language={language} onBack={backToHub} onAttempt={recordAttempt} onNarrationMoment={playNarrationMoment} />}
      {screen === "bluff" && <BluffScreen tier={tier} language={language} onBack={backToHub} onNarrationMoment={playNarrationMoment} />}
      {screen === "feed" && <FeedScreen tier={tier} language={language} onBack={backToHub} onNarrationMoment={playNarrationMoment} />}
      {screen === "dial" && <DialScreen tier={tier} language={language} onBack={backToHub} onNarrationMoment={playNarrationMoment} />}
      {screen === "relay" && <RelayScreen tier={tier} language={language} onBack={backToHub} onNarrationMoment={playNarrationMoment} />}

      {screen === "ask" && (
        <AskSarasScreen
          tier={tier}
          language={language}
          onBack={backToHub}
          visitorUserId={visitorUserId}
          clientSessionId={visitorSessionId}
          exhibitVersion={EXHIBIT_VERSION}
          conversationStarted={askConversationStarted}
          onConversationStarted={() => setAskConversationStarted(true)}
          audioEnabled={audioEnabled}
          onBeforeQuestionAudio={stopAIExhibitV2FixedNarration}
          onQuestionAsked={(item) =>
            logEvent("ask_ai_question", {
              stationId: "ask",
              screenId: "ask",
              payload: { curated_question_id: item?.id || null, language },
            })
          }
        />
      )}

      {screen === "scoreboard" && (
        <ScoreboardScreen
          tier={tier}
          language={language}
          onBack={backToHub}
          contentVersion={CONTENT_VERSION}
          kioskId={kioskId}
          studentEmail={visitorUserId}
        />
      )}

      {screen === "library" && (
        <LibraryBridgeScreen
          language={language}
          onBack={backToHub}
          onOpenAsk={() => navigateTo("ask")}
          visitorUserId={visitorUserId}
          clientSessionId={visitorSessionId}
        />
      )}

      <View style={[s.audioToggle, showLanguageToggle && s.audioToggleWithLanguage]}>
        <Pressable
          onPress={toggleAudio}
          style={[s.audioOption, audioEnabled && s.audioOptionActive]}
        >
          <Text style={[s.audioText, audioEnabled && s.audioTextActive]}>
            {audioEnabled ? tx(language, "🔊 Audio on") : tx(language, "🔈 Audio")}
          </Text>
        </Pressable>
      </View>

      {showLanguageToggle ? (
        <View style={s.languageToggle}>
          <Pressable
            onPress={() => changeLanguage("en")}
            style={[s.languageOption, language === "en" && s.languageOptionActive]}
          >
            <Text style={[s.languageText, language === "en" && s.languageTextActive]}>English</Text>
          </Pressable>
          <Pressable
            onPress={() => changeLanguage("es")}
            style={[s.languageOption, language === "es" && s.languageOptionActive]}
          >
            <Text style={[s.languageText, language === "es" && s.languageTextActive]}>Español</Text>
          </Pressable>
        </View>
      ) : null}

      {idleWarning ? (
        <View pointerEvents="none" style={s.idleToast}>
          <Text style={s.idleToastText}>{tx(language, "Still exploring? Touch anywhere to continue.")}</Text>
        </View>
      ) : null}
    </ExhibitV2Stage>
    </ExhibitLocaleProvider>
  );
}

const s = StyleSheet.create({
  audioToggle: {
    position: "absolute",
    top: 14,
    right: 34,
    zIndex: 60,
  },
  audioToggleWithLanguage: {
    right: 278,
  },
  audioOption: {
    minWidth: 138,
    minHeight: 40,
    paddingHorizontal: 16,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "#29405F",
    backgroundColor: "#091321",
    alignItems: "center",
    justifyContent: "center",
  },
  audioOptionActive: {
    borderColor: "#4FD1FF",
    backgroundColor: "#0E2237",
  },
  audioText: {
    color: "#8FA5BC",
    fontSize: 16,
    fontWeight: "800",
  },
  audioTextActive: {
    color: "#F4F8FC",
  },
  languageToggle: {
    position: "absolute",
    top: 14,
    right: 34,
    zIndex: 50,
    flexDirection: "row",
    padding: 4,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "#29405F",
    backgroundColor: "#091321",
  },
  languageOption: {
    minWidth: 104,
    minHeight: 40,
    paddingHorizontal: 16,
    borderRadius: 999,
    alignItems: "center",
    justifyContent: "center",
  },
  languageOptionActive: {
    backgroundColor: "#0E3A50",
    borderWidth: 1,
    borderColor: "#4FD1FF",
  },
  languageText: {
    color: "#8FA5BC",
    fontSize: 16,
    fontWeight: "800",
  },
  languageTextActive: {
    color: "#F4F8FC",
  },
  idleToast: {
    position: "absolute",
    right: 54,
    bottom: 46,
    paddingHorizontal: 26,
    paddingVertical: 17,
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#4FD1FF",
    backgroundColor: "#0E2237",
  },
  idleToastText: {
    color: "#F4F8FC",
    fontSize: 20,
    fontWeight: "900",
  },
});
